import { trpcClient } from "@tokenterminal/core/utils/trpc"
import { fetchAtom } from "data/api/useFetch"
import { atom, useAtomValue } from "jotai"
import { useState } from "react"
import { type ProjectMeta } from "utils/trpc"

const ENDPOINT = "/v3/internal/projects" as const

type AvailableFields = keyof Pick<
  ProjectMeta,
  "data_id" | "id" | "slug" | "name"
>

export function convertProjectsToDictionary(
  projects: Array<ProjectMeta>,
  key: AvailableFields = "slug"
): Record<ProjectMeta[typeof key], ProjectMeta> {
  return projects.reduce(
    (acc, project) => {
      acc[project[key]] = project
      return acc
    },
    {} as Record<ProjectMeta[typeof key], ProjectMeta>
  )
}

export function createIdToSlugDictionary(projects: Array<ProjectMeta>) {
  return projects.reduce(
    (acc, project) => {
      acc[project.id] = project.slug

      return acc
    },
    {} as Record<string, string>
  )
}

export const projectsMetaAtom = atom(async (get) => {
  const result = await trpcClient.projects.getProjects.query()

  return result?.data ?? []
})

const projectsWithPlaceholdersMetaAtom = atom(async (get) => {
  const { ok: payload } = await get(
    fetchAtom((ENDPOINT + "?include=placeholder") as typeof ENDPOINT)
  )

  return payload?.data ?? []
})

export function useProjectsMeta() {
  return useAtomValue(projectsMetaAtom)
}

export function useProjectsWithPlaceholdersMeta() {
  return useAtomValue(projectsWithPlaceholdersMetaAtom)
}

export function useProjectMetaAsDictionaryBySlug() {
  const projects = useProjectsMeta()
  const [dictionary] = useState(convertProjectsToDictionary(projects))

  return dictionary
}

export function useProjectMetaAsDictionaryByDataId() {
  const projects = useProjectsMeta()
  const [dictionary] = useState(
    convertProjectsToDictionary(projects, "data_id")
  )

  return dictionary
}

export function useProjectMeta(project: string) {
  const projects = useProjectsMeta()

  return projects.find((p) => p.slug === project)
}
